import { useEffect, useState } from 'react';
import { SearchActions, SettingsActions } from './sagas/actions';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Favorites from './downloading/Favorites';
import Menu from './Menu';
import { toast } from 'sonner';
import { Menu as MenuIcon, Moon, Search, Settings, ShoppingBasket, Sun, Trash2, X } from 'lucide-react';
import { useTheme } from './hooks/useTheme';
import niozLogo from './assets/nioz-logo-white.svg';
import uuLogo from './assets/uu-logo-color-dark.png';

/**
 * The single application chrome: a sticky navy bar carrying the wordmark, the
 * main navigation, a compact global search affordance and the basket. Sits on
 * every route — the home page adds a hero beneath it rather than a second bar.
 */
export const Header = () => {
    const dispatch = useDispatch();
    const showStatusSelector = useSelector((state: any) => state.settings.showStatus);
    const [showStatus, setShowStatus] = useState(showStatusSelector);
    const basketSize = useSelector((state: any) => state.basket.basket.length);
    const navigate = useNavigate();
    const location = useLocation();
    const { theme, toggleTheme } = useTheme();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    useEffect(() => {
        setShowStatus(showStatusSelector);
    }, [showStatusSelector]);

    useEffect(() => setMobileNavOpen(false), [location.pathname]);

    const isSearchScreen =
        location.pathname === '/' ||
        location.pathname.startsWith('/results') ||
        location.pathname.startsWith('/search');

    // `/` focuses search from anywhere, the way the redesign specifies.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const el = e.target as HTMLElement | null;
            const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
            if (e.key === '/' && !typing) {
                e.preventDefault();
                const input = document.getElementById('search') as HTMLInputElement | null;
                if (input) input.focus();
                else navigate('/results');
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [navigate]);

    const handleToggleStatus = () => {
        dispatch({ type: SettingsActions.TOGGLE_SHOW_STATUS });
    };

    const handleNavBasket = () => navigate('/basket');

    const handleNavHome = () => {
        dispatch({ type: SearchActions.SET_URL, payload: '/' });
        dispatch({ type: SearchActions.RESET_SEARCH });
        navigate('/');
    };

    const clearBrowser = () => {
        localStorage.clear();
        sessionStorage.clear();
        toast.success('Browser storage cleared', {
            description: 'Local and session storage have been cleared. The page will reload.',
            duration: 2000,
        });
        setTimeout(() => window.location.reload(), 2000);
    };

    // Shared look for the chrome's ghost controls; width is set per instance.
    const chromeBtnBase =
        'flex h-8 items-center justify-center gap-2 rounded-md border border-white/20 text-white/80 ' +
        'transition-colors duration-150 hover:bg-white/10 hover:text-white';
    const chromeBtn = `${chromeBtnBase} w-8`;

    return (
        <header className="fixed top-0 left-0 right-0 z-50 w-full bg-chrome">
            <div className="mx-auto flex h-[var(--nz-header-h)] w-full max-w-[1680px] items-center gap-4 px-3 sm:px-6">

                {/* ── wordmark ─────────────────────────────────────────────── */}
                <button
                    type="button"
                    onClick={handleNavHome}
                    className="flex shrink-0 items-center gap-3 text-left md:gap-4"
                    title="Dutch Marine Science Portal — home"
                >
                    {/* Sized to match the NIOZ Data Repository (CKAN) header: 76px, 52px on phones. */}
                    <img src={niozLogo} alt="NIOZ" className="h-[52px] w-auto md:h-[76px]" />
                    {/* The bar is navy in both themes, so UU always uses its light-on-dark logo. */}
                    <span className="hidden h-12 w-px bg-white/25 md:block" />
                    <img src={uuLogo} alt="Utrecht University" className="hidden h-14 w-auto md:block" />
                    {/* Between lg and xl the menu needs the room, so only the logos show. */}
                    <span className="hidden flex-col gap-1 leading-[1.15] sm:flex lg:hidden xl:flex">
                        <span className="text-[18px] font-bold tracking-[.005em] text-white xl:text-[22px]">
                            Dutch Marine Science Portal
                        </span>
                        <span className="text-[11px] font-semibold tracking-[.12em] text-white/60 xl:text-[12px]">
                            NIOZ · UTRECHT UNIVERSITY
                        </span>
                    </span>
                    <span className="text-[18px] font-bold text-white sm:hidden">DMSP</span>
                </button>

                {/* ── primary navigation (desktop) ─────────────────────────── */}
                <div className="ml-2 hidden lg:block">
                    <Menu />
                </div>

                {/* ── compact global search, off the search screens ────────── */}
                {!isSearchScreen && (
                    <button
                        type="button"
                        onClick={() => navigate('/results')}
                        className="ml-auto hidden h-[34px] w-[320px] items-center gap-2.5 rounded-md border border-white/20 bg-white/10 px-3 text-sm text-white/60 transition-colors hover:bg-white/[.18] hover:text-white/80 xl:flex"
                    >
                        <Search className="h-[15px] w-[15px]" />
                        <span className="truncate">Search datasets, cruises, DOIs…</span>
                        <span className="nz-mono ml-auto rounded border border-white/25 px-1.5 text-[11px]">/</span>
                    </button>
                )}

                {/* ── actions ──────────────────────────────────────────────── */}
                <div className="ml-auto flex items-center gap-2">
                    <button
                        type="button"
                        onClick={toggleTheme}
                        className={chromeBtn}
                        title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                        aria-label="Toggle colour theme"
                    >
                        {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>

                    <Favorites />

                    <button
                        type="button"
                        onClick={handleNavBasket}
                        title="Download basket"
                        className={
                            basketSize > 0
                                ? 'flex h-8 items-center gap-2 rounded-md bg-warm-fill px-3 text-sm font-semibold text-[#00195a] transition-[filter] duration-150 hover:brightness-[.94]'
                                : `${chromeBtnBase} px-2.5`
                        }
                    >
                        <ShoppingBasket className="h-[15px] w-[15px]" />
                        {basketSize > 0 && (
                            <>
                                <span className="hidden lg:inline">Basket</span>
                                <span className="nz-mono flex h-[18px] min-w-[18px] items-center justify-center rounded bg-[rgba(0,25,90,.16)] px-1.5 text-[11px] font-bold">
                                    {basketSize}
                                </span>
                            </>
                        )}
                    </button>

                    {!import.meta.env.PROD && (
                        <>
                            <button
                                type="button"
                                onClick={handleToggleStatus}
                                className={`${chromeBtn} hidden sm:flex`}
                                title={showStatus ? 'Hide status panel' : 'Show status panel'}
                            >
                                {showStatus ? <X className="h-4 w-4" /> : <Settings className="h-4 w-4" />}
                            </button>
                            <button
                                type="button"
                                onClick={clearBrowser}
                                className={`${chromeBtn} hidden sm:flex`}
                                title="Clear browser storage"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </>
                    )}

                    <button
                        type="button"
                        onClick={() => setMobileNavOpen((v) => !v)}
                        className={`${chromeBtn} lg:hidden`}
                        aria-expanded={mobileNavOpen}
                        aria-label="Toggle navigation"
                    >
                        {mobileNavOpen ? <X className="h-4 w-4" /> : <MenuIcon className="h-4 w-4" />}
                    </button>
                </div>
            </div>

            {/* ── mobile navigation drawer ─────────────────────────────────── */}
            {mobileNavOpen && (
                <div className="border-t border-white/10 bg-chrome px-3 pb-3 lg:hidden">
                    <Menu />
                </div>
            )}

            <div className="nz-bar" />
        </header>
    );
};
